/*
 * The year slider's bounds are recalculated whenever the dropdown options come
 * back for the current filters - pick a client and the available years narrow.
 *
 * Recalculating the bounds must not throw away what the user picked. The slider
 * previously reset to the full range every time, so a chosen 2021-2026 stayed in
 * the query while the slider snapped back to the whole span, and the two
 * disagreed. Keep the selection and clamp it into the new bounds instead.
 */

const isRange = (value) =>
  Array.isArray(value) && value.length === 2 &&
  Number.isFinite(value[0]) && Number.isFinite(value[1])

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

/**
 * Work out the slider selection to use once the bounds have changed.
 *
 * @param {object} options
 * @param {Array<number>} [options.previous] the selection currently shown
 * @param {Array<number>} [options.previousBounds] the bounds it was chosen against
 * @param {number} options.min new lower bound
 * @param {number} options.max new upper bound
 * @returns {Array<number>} the selection to apply
 */
const resolveYearSelection = ({ previous, previousBounds, min, max }) => {
  if (!isRange(previous)) {
    return [min, max]
  }
  // The user never narrowed anything - the selection was just the old full
  // span - so follow the new bounds rather than pinning them to the old ones.
  if (isRange(previousBounds) &&
      previous[0] === previousBounds[0] && previous[1] === previousBounds[1]) {
    return [min, max]
  }
  const from = clamp(Math.min(previous[0], previous[1]), min, max)
  const to = clamp(Math.max(previous[0], previous[1]), min, max)
  return [from, to]
}

export default { resolveYearSelection }
